import { supabase } from '../../lib/supabase';
export class SalesSettingsService {
    /**
     * Get settings for a user in an organization
     */
    async getSettings(organizationId, userId) {
        try {
            const { data, error } = await supabase
                .from('sales_settings')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('user_id', userId)
                .single();
            if (error) {
                // If settings don't exist, create default settings
                if (error.code === 'PGRST116') {
                    return await this.createDefaultSettings(organizationId, userId);
                }
                console.error('Error fetching settings:', error);
                return null;
            }
            return this.transformSettings(data);
        }
        catch (error) {
            console.error('Error in getSettings:', error);
            return null;
        }
    }
    /**
     * Create default settings for a user
     */
    async createDefaultSettings(organizationId, userId) {
        try {
            const { data, error } = await supabase
                .from('sales_settings')
                .insert({
                organization_id: organizationId,
                user_id: userId,
                // Default values
                email_notifications: true,
                push_notifications: true,
                daily_digest: true,
                follow_up_reminders: true,
                deal_updates: true,
                new_leads: true,
                email_sync_enabled: false,
                calendar_sync_enabled: false,
                google_integration: false,
                outlook_integration: false,
                ai_assistant_enabled: true,
                auto_follow_up: false,
                lead_scoring_enabled: true,
                ai_email_templates: true,
                theme: 'dark',
                accent_color: '#F59E0B',
                compact_view: false,
                share_analytics: true,
                track_activity: true
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating default settings:', error);
                return null;
            }
            return this.transformSettings(data);
        }
        catch (error) {
            console.error('Error in createDefaultSettings:', error);
            return null;
        }
    }
    /**
     * Update user settings
     */
    async updateSettings(organizationId, userId, updates) {
        try {
            const { data, error } = await supabase
                .from('sales_settings')
                .update(updates)
                .eq('organization_id', organizationId)
                .eq('user_id', userId)
                .select()
                .single();
            if (error) {
                console.error('Error updating settings:', error);
                return null;
            }
            return this.transformSettings(data);
        }
        catch (error) {
            console.error('Error in updateSettings:', error);
            return null;
        }
    }
    /**
     * Update notification settings
     */
    async updateNotifications(organizationId, userId, notifications) {
        try {
            const { error } = await supabase
                .from('sales_settings')
                .update({
                email_notifications: notifications.emailNotifications,
                push_notifications: notifications.pushNotifications,
                daily_digest: notifications.dailyDigest,
                follow_up_reminders: notifications.followUpReminders,
                deal_updates: notifications.dealUpdates,
                new_leads: notifications.newLeads
            })
                .eq('organization_id', organizationId)
                .eq('user_id', userId);
            if (error) {
                console.error('Error updating notifications:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateNotifications:', error);
            return false;
        }
    }
    /**
     * Update integration settings
     */
    async updateIntegrations(organizationId, userId, integrations) {
        try {
            const { error } = await supabase
                .from('sales_settings')
                .update({
                email_sync_enabled: integrations.emailSyncEnabled,
                calendar_sync_enabled: integrations.calendarSyncEnabled,
                google_integration: integrations.googleIntegration,
                outlook_integration: integrations.outlookIntegration
            })
                .eq('organization_id', organizationId)
                .eq('user_id', userId);
            if (error) {
                console.error('Error updating integrations:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateIntegrations:', error);
            return false;
        }
    }
    /**
     * Update AI settings
     */
    async updateAISettings(organizationId, userId, aiSettings) {
        try {
            const { error } = await supabase
                .from('sales_settings')
                .update({
                ai_assistant_enabled: aiSettings.aiAssistantEnabled,
                auto_follow_up: aiSettings.autoFollowUp,
                lead_scoring_enabled: aiSettings.leadScoringEnabled,
                ai_email_templates: aiSettings.aiEmailTemplates
            })
                .eq('organization_id', organizationId)
                .eq('user_id', userId);
            if (error) {
                console.error('Error updating AI settings:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateAISettings:', error);
            return false;
        }
    }
    /**
     * Update appearance settings
     */
    async updateAppearance(organizationId, userId, appearance) {
        try {
            const { error } = await supabase
                .from('sales_settings')
                .update({
                theme: appearance.theme,
                accent_color: appearance.accentColor,
                compact_view: appearance.compactView
            })
                .eq('organization_id', organizationId)
                .eq('user_id', userId);
            if (error) {
                console.error('Error updating appearance:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateAppearance:', error);
            return false;
        }
    }
    /**
     * Transform database row to SalesSettings type
     */
    transformSettings(row) {
        return {
            id: row.id,
            organizationId: row.organization_id,
            userId: row.user_id,
            emailNotifications: row.email_notifications,
            pushNotifications: row.push_notifications,
            dailyDigest: row.daily_digest,
            followUpReminders: row.follow_up_reminders,
            dealUpdates: row.deal_updates,
            newLeads: row.new_leads,
            emailSyncEnabled: row.email_sync_enabled,
            calendarSyncEnabled: row.calendar_sync_enabled,
            googleIntegration: row.google_integration,
            outlookIntegration: row.outlook_integration,
            aiAssistantEnabled: row.ai_assistant_enabled,
            autoFollowUp: row.auto_follow_up,
            leadScoringEnabled: row.lead_scoring_enabled,
            aiEmailTemplates: row.ai_email_templates,
            theme: row.theme,
            accentColor: row.accent_color,
            compactView: row.compact_view,
            shareAnalytics: row.share_analytics,
            trackActivity: row.track_activity,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
        };
    }
}
// Export singleton instance
export const salesSettingsService = new SalesSettingsService();
